import {api} from "@/data/api";
import {OptionGroup, ProductOption} from "@/data/entities/option";
import {OptionGroupEditValues, OptionGroupType, ProductOptionEditValues} from "@/data/entities/menuEditor";
import {OptionGroupsLookup} from "@widgets/menuEditor/optionGroups/optionGroupsLookup";

/**
 * An option in the modal: an existing option, or a new one that's created on
 * save.
 */
export type DraftOption =
    | { key: string, kind: "existing", option: ProductOption }
    | { key: string, kind: "new", values: ProductOptionEditValues };

export type OptionGroupFormValues = {
    name: string,
    type: OptionGroupType,
    maxOptions: number,
};

let newDraftCounter = 0;

export const newDraftKey = (): string => `new-${newDraftCounter++}`;

export const toOptionGroupFormValues = (group: OptionGroup | null): OptionGroupFormValues => group === null
    ? {name: "", type: "optional", maxOptions: 0}
    : {name: group.name, type: group.type as OptionGroupType, maxOptions: group.maxOptions};

export const toDraftOptions = (group: OptionGroup | null, lookup: OptionGroupsLookup): DraftOption[] => (
    group === null
        ? []
        : group.optionIds
            .map(optionId => lookup.options.get(optionId))
            .filter((option): option is ProductOption => option !== undefined)
            .map((option): DraftOption => ({key: `option-${option.id}`, kind: "existing", option}))
);

export const draftName = (draft: DraftOption): string => (
    draft.kind === "existing" ? draft.option.name : draft.values.name
);

export const sortDraftsAlphabetically = (drafts: DraftOption[]): DraftOption[] => (
    [...drafts].sort((a, b) => draftName(a).localeCompare(draftName(b), "nl", {numeric: true, sensitivity: "base"}))
);

const toOptionGroupRequest = (form: OptionGroupFormValues, optionIds?: number[]): OptionGroupEditValues => ({
    name: form.name.trim(),
    type: form.type,
    maxOptions: form.type === "required" ? 0 : form.maxOptions,
    optionIds,
});

/**
 * Saves the group (creates it when new), creates the new options and then
 * sets all options in their order. Options left out are removed from the
 * group (they still exist).
 */
export const saveOptionGroup = async (
    restaurantId: number,
    groupId: number | null,
    form: OptionGroupFormValues,
    drafts: DraftOption[],
): Promise<OptionGroup> => {
    const id: number = groupId ?? (await api.menuEditor.createOptionGroup(restaurantId, toOptionGroupRequest(form))).id;

    // One by one: the new options get their place in the last step
    const optionIds: number[] = [];
    for (const draft of drafts) {
        optionIds.push(draft.kind === "existing"
            ? draft.option.id
            : (await api.menuEditor.addOption(restaurantId, id, draft.values)).id);
    }

    return api.menuEditor.updateOptionGroup(restaurantId, id, toOptionGroupRequest(form, optionIds));
};